import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Toast, toastError } from '@/plugins/toast.js'
import { getErrorMessage } from '@/utils/apiError'
import { normalizeEmail } from '@/utils/formatters'
import { authApi } from '@/services/api'
import { getItem, setItem, removeItem, getJSON, isPersistentStorageAvailable } from '@/utils/safeStorage'
// Se importa la instancia y no se usa useRouter(): si el store se creaba durante la
// navegación inicial (el redirect de "/" llama a useAuthStore mientras se instala el
// router), la inyección aún no existía y router quedaba undefined. El login respondía
// bien pero router.push fallaba y se mostraba "Error al iniciar sesión".
// El import es circular con '@/router', pero ambos lados solo se usan dentro de funciones.
import router from '@/router'

export const useAuthStore = defineStore('auth', () => {
 // const toast = useToast()
  const user = ref(getJSON('user', null))
  const token = ref(getItem('token'))
  const refreshToken = ref(getItem('refreshToken'))
  const isLoading = ref(false)
  const initialized = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)

  /**
   * El portal se decide por el ámbito del rol, no por su nombre, para que los
   * roles creados desde el gestor (ej. SUPERVISOR) funcionen igual que ADMIN.
   * El fallback por nombre cubre las sesiones abiertas antes de la migración.
   */
  const roleScope = computed(() => {
    if (user.value?.roleScope) return user.value.roleScope
    if (user.value?.role === 'ADMIN') return 'admin'
    if (user.value?.role === 'SUPPLIER') return 'supplier'
    return null
  })

  const isAdmin = computed(() => roleScope.value === 'admin')
  const isSupplier = computed(() => roleScope.value === 'supplier')
  const isContabilidad = computed(() => isAdmin.value && user.value?.department === 'CONTABILIDAD')

  /** Permisos efectivos del usuario (nombres, tal como los define el rol). */
  const permissions = computed(() => user.value?.permissions || [])

  /**
   * ¿El usuario tiene el permiso indicado?
   * Acepta un string o un array (en ese caso basta con uno: OR).
   *
   * Nota: mientras el backend no devuelva permisos (sesión vieja en
   * localStorage), un admin no queda bloqueado de su propio portal.
   */
  const can = (permission) => {
    const required = Array.isArray(permission) ? permission : [permission]

    if (!user.value) return false

    // Compatibilidad: sesión previa a la migración, sin lista de permisos.
    if (!user.value.permissions) return isAdmin.value

    return required.some(p => permissions.value.includes(p))
  }

  /** Exige todos los permisos indicados (AND). */
  const canAll = (permission) => {
    const required = Array.isArray(permission) ? permission : [permission]

    if (!user.value) return false
    if (!user.value.permissions) return isAdmin.value

    return required.every(p => permissions.value.includes(p))
  }

  /** ¿Tiene acceso al módulo? `module` es la clave corta, ej. 'invoices'. */
  const canAccessModule = (module) => can(`module.${module}`)

  /** Ruta inicial según el ámbito del rol del usuario recién autenticado. */
  const homeRouteFor = (userData) => {
    const scope = userData?.roleScope || (userData?.role === 'ADMIN' ? 'admin' : 'supplier')
    return scope === 'admin' ? '/admin' : '/supplier'
  }
  const userDepartment = computed(() => user.value?.department)
  const userName = computed(() => user.value ? `${user.value.firstName} ${user.value.lastName}` : '')
  const userEmail = computed(() => user.value?.email || '')
  const supplierId = computed(() => user.value?.supplier?.id || null)
  const userSuppliers = computed(() => user.value?.suppliers || [])

  const initializeAuth = async () => {
    if (initialized.value) return

    initialized.value = true

    if (token.value && user.value) {
      try {
        const response = await authApi.getProfile()
        user.value = response.data.data
        setItem('user', JSON.stringify(response.data.data))
      } catch (error) {
        if (error.response?.status === 401) {
          token.value = null
          user.value = null
          refreshToken.value = null
          removeItem('token')
          removeItem('refreshToken')
          removeItem('user')
        }
      }
    }
  }

  /**
   * Recarga el perfil para traer los permisos vigentes. Se llama después de
   * editar roles: si el admin cambió su propio rol, la UI se ajusta sin
   * necesidad de volver a iniciar sesión.
   */
  const refreshPermissions = async () => {
    if (!token.value) return null

    try {
      const response = await authApi.getProfile()
      user.value = response.data.data
      setItem('user', JSON.stringify(response.data.data))
      return user.value
    } catch (error) {
      return null
    }
  }

  const login = async credentials => {
    isLoading.value = true

    try {
      // El correo se normaliza aquí también: los teclados móviles agregan
      // espacios y mayúsculas automáticas que hacían fallar el login.
      const payload = {
        ...credentials,
        email: normalizeEmail(credentials.email)
      }

      const response = await authApi.login(payload)
      const { user: userData, token: accessToken, refreshToken: refreshTokenData } = response.data.data

      token.value = accessToken
      refreshToken.value = refreshTokenData
      user.value = userData

      const stored = setItem('token', accessToken)
      setItem('refreshToken', refreshTokenData)
      setItem('user', JSON.stringify(userData))

      // Modo privado / datos de sitio bloqueados: la sesión vive solo en memoria.
      if (!stored && !isPersistentStorageAvailable()) {
        Toast.fire({
          icon: 'warning',
          title: 'Tu navegador bloquea el almacenamiento: la sesión se cerrará al recargar la página'
        })
      }

      if (userData.mustChangePassword) {
        Toast.fire({
          icon: 'warning',
          title: 'Debes cambiar tu contraseña temporal'
        });
        router.push('/change-password')
      } else {
        Toast.fire({
          icon: 'success',
          title: `Bienvenido, ${userData.firstName}!`
        });
        router.push(homeRouteFor(userData))
      }

      return { success: true }
    } catch (error) {
      // Distingue correo no registrado, contraseña incorrecta, usuario inactivo,
      // falta de red, etc.: el backend manda el motivo en `message`.
      const errorMessage = getErrorMessage(error, 'Error al iniciar sesión')

      // Traza en la consola del dispositivo: es lo que permite diagnosticar
      // fallos que solo se reproducen en el navegador del celular.
      console.error('[auth.login] fallo', {
        status: error.response?.status ?? null,
        data: error.response?.data ?? null,
        code: error.code ?? null,
        message: error.message,
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        persistentStorage: isPersistentStorageAvailable()
      })

      toastError(errorMessage)

      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      if (token.value) {
        await authApi.logout()
      }
    } catch (error) {
    } finally {
      user.value = null
      token.value = null
      refreshToken.value = null
      removeItem('token')
      removeItem('refreshToken')
      removeItem('user')

      Toast.fire({
        icon: 'info',
        title: 'Sesión cerrada correctamente'
      });
      router.push('/login')
    }
  }


  const setTokens = (accessToken, refreshTokenData) => {
    token.value = accessToken
    refreshToken.value = refreshTokenData
    setItem('token', accessToken)
    setItem('refreshToken', refreshTokenData)
  }

  const changePassword = async (currentPassword, newPassword) => {
    isLoading.value = true

    try {
      const response = await authApi.changePassword({ currentPassword, newPassword })
      Toast.fire({
        icon: 'success',
        title: response.data.message || 'Contraseña actualizada correctamente'
      });
      return { success: true }
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Error al cambiar contraseña')
      toastError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const forceChangePassword = async (currentPassword, newPassword) => {
    isLoading.value = true
    try {
      await authApi.forceChangePassword({ currentPassword, newPassword })
      user.value = { ...user.value, mustChangePassword: false }
      setItem('user', JSON.stringify(user.value))
      Toast.fire({
        icon: 'success',
        title: 'Contraseña actualizada correctamente'
      });
      router.push(homeRouteFor(user.value))
      return { success: true }
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Error al cambiar contraseña')
      toastError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const setLoading = (value) => {
    isLoading.value = value;
  };

  const setError = (message) => {
    if (message) toastError(message)
  };

  const validateUserWithBackend = async (userInfo) => {
    try {
      const response = await authApi.loginMicrosoft(userInfo);
      const { user: userData, token: accessToken, refreshToken: refreshTokenData } = response.data.data;

      token.value = accessToken;
      refreshToken.value = refreshTokenData;
      user.value = userData;

      setItem('token', accessToken);
      setItem('refreshToken', refreshTokenData);
      setItem('user', JSON.stringify(userData));

      Toast.fire({
        icon: 'success',
        title: `¡Bienvenido, ${userData.firstName}!`
      });

      router.push(homeRouteFor(userData));

      return { success: true };
    } catch (error) {
      // Un solo toast y siempre un texto: antes los errores de validación se
      // devolvían como arreglo y la vista terminaba mostrando "[object Object]".
      const errorMessage = getErrorMessage(error, 'Error al validar usuario Microsoft');
      toastError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  return {
    user,
    token,
    refreshToken,
    isLoading,
    initialized,

    isAuthenticated,
    isAdmin,
    isContabilidad,
    isSupplier,
    roleScope,
    permissions,
    userName,
    userEmail,
    userDepartment,
    supplierId,
    userSuppliers,

    can,
    canAll,
    canAccessModule,
    homeRouteFor,

    initializeAuth,
    refreshPermissions,
    login,
    logout,
    changePassword,
    forceChangePassword,
    setTokens,

    setLoading,
    setError,
    validateUserWithBackend
  }
})
